// ── ReferralSourcePage — detail with Quick View / Referred Leads / Referred Opportunities / Activity ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, Icon, PageLink, STh, SectionHeader, Spinner, StatCard, TabBtn, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { SalesActivityPanel, LastTouchLine } from "../../../shared/SalesActivityPanel";
import { EmailActivityPanel } from "../../../shared/EmailActivityPanel";
import { ReleaseReferralSourceModal, canRelease } from "../components/ReleaseReferralSource";

export const ReferralSourcePage = () => {
  const { api, role, view, tab, setTab, setModal, setConfirm, delRec, currentUser, go } = useApp();
  const [releasing, setReleasing] = useState(false);
  const [rs, setRs] = useState(null);
  const [leads, setLeads] = useState([]);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Just the headline, fetched with ?summary=1 so Quick View can show WHEN and
  // WHAT the last contact was without loading the whole history behind it. The
  // Activity tab refreshes this through onLoaded, so logging a call updates the
  // Quick View line without a page reload.
  const [lastTouch, setLastTouch] = useState(null);

  const rsId = view.referralsourceid;

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("getReferralSources", { id: rsId }),
      api.get("getLeads", { referralsourceid: rsId }),
      api.get("getOpportunities", { referralsourceid: rsId }),
    ]).then(([r, l, o]) => {
      setRs((Array.isArray(r) ? r[0] : r) || null);
      setLeads(Array.isArray(l) ? l : []);
      setOpps(Array.isArray(o) ? o : []);
      setLoading(false);
    }).catch(e => { setErr(e.message); setLoading(false); });

    // Deliberately NOT inside the Promise.all: the three calls above decide
    // whether the page renders at all, and a missing activity view (migration
    // not run yet) must not blank out a referral source. It fails quietly to
    // "no contact on record", which is also what it says when there genuinely
    // is none.
    api.get("getSalesActivity", { entitytype: "ReferralSource", entityid: rsId, summary: 1 })
      .then(d => setLastTouch((d && d.lasttouch) || null))
      .catch(() => setLastTouch(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rsId]);

  const { sorted: sortedLeads, sortKey: lKey, sortDir: lDir, toggle: lToggle } = useSortable(leads, 'createdat', 'desc');
  const { sorted: sortedOpps, sortKey: oKey, sortDir: oDir, toggle: oToggle } = useSortable(opps, 'stage_order', 'asc');

  // Convert a referred lead in place — reload and jump to this source's opps.
  const convert = (x) => setConfirm({
    msg: "Convert \"" + (x.companyname || "this lead") + "\" into an Account + Contact + Opportunity? The lead will be archived.",
    onConfirm: () => {
      api.post("convertLead", { id: x.id })
        .then(() => { setConfirm(null); load(); setTab("opportunities"); })
        .catch(e => { setConfirm(null); setErr(e.message); });
    }
  });

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!rs) return <ErrorMsg msg="Referral source not found." />;

  // Matches the Panel heading on the portfolio Account overview tab, so two
  // cards side by side read as two sections rather than one split table.
  const Panel = ({ title }) => (
    <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em",
                  marginBottom:12, paddingBottom:6, borderBottom:"1px solid "+B.cyanLight }}>{title}</div>
  );

  const Row = ({ label, value }) => (
    <div style={{ display:"flex", justifyContent:"space-between", gap:8, fontSize:13, padding:"4px 0" }}>
      <span style={{ color:"var(--color-text-secondary)", flexShrink:0 }}>{label}</span>
      <span style={{ textAlign:"right" }}>{value}</span>
    </div>
  );

  const TABS = ["quickview", "leads", "opportunities", "activity", "email"];
  const tabLabel = t => t === "quickview" ? "Quick View" : t === "leads" ? "Referred Leads"
    : t === "opportunities" ? "Referred Opportunities" : t === "email" ? "Email" : "Activity";
  const activeTab = TABS.includes(tab) ? tab : "quickview";

  return <>
    <SectionHeader title={rs.company_name || rs.full_name || "Referral Source"} actions={[
      // Owner only: give it up. After this the rep can no longer open it, so
      // the page goes back to the list rather than reloading into a 403.
      // Red, and set apart from Edit with a divider so it is not one slip of
      // the mouse away from the button people press every day.
      canRelease(rs, currentUser, role) ? (
        <span key="release" style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 14,
                                     paddingRight: 14, borderRight: "1px solid var(--color-border-secondary)" }}>
          <Btn variant="danger" title="Give up ownership — asks for a reason first" onClick={() => setReleasing(true)}>Remove me as owner</Btn>
        </span>) : null,
      role.canEdit ? <Btn key="edit" variant="primary" onClick={() => setModal({ type: "referralsource-edit", rec: rs, onDone: load })}>Edit</Btn> : null
    ]} />
    {releasing && <ReleaseReferralSourceModal rs={rs} onClose={() => setReleasing(false)}
      onEditInstead={() => { setReleasing(false); setModal({ type: "referralsource-edit", rec: rs, onDone: load }); }}
      onDone={() => { setReleasing(false); if (role.isAdmin || role.isbdomanager) load(); else go("sales-referral-sources"); }} />}

    <Card>
      <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
        <div style={{ minWidth:260 }}>
          <Row label="Company" value={rs.company_name || "—"} />
          <Row label="Contact" value={rs.full_name || "—"} />
          <Row label="Type" value={[rs.contact_type, rs.contact_subtype].filter(Boolean).join(" / ") || "—"} />
          <Row label="Status" value={<Badge s={rs.status} />} />
          <Row label="Owner" value={rs.ownerName || "—"} />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3, minmax(110px, 1fr))", gap:12, flex:1 }}>
          <StatCard label="Rating" value={rs.rating || "—"} />
          <StatCard label="Referred Leads" value={leads.length} />
          <StatCard label="Referred Opportunities" value={opps.length} />
        </div>
      </div>
    </Card>

    <div style={{ marginTop:16 }}>
      <div style={{ display:"flex", flexWrap:"wrap", gap:3, paddingLeft:0, position:"relative", zIndex:1, marginTop:4, borderBottom:"4px solid "+B.cyan }}>
        {TABS.map(t => <TabBtn key={t} id={t} current={activeTab} label={tabLabel(t)} onClick={setTab} />)}
      </div>
      <div style={{ background:"var(--color-background-primary)", border:"0.5px solid var(--color-border-tertiary)", borderTop:"none", borderRadius:"0 0 8px 8px", padding:16, position:"relative", zIndex:2 }}>

        {/* Two cards at a third of the page each, so the labels sit next to their
            values instead of being flung to opposite edges of a wide screen.
            flex "0 1 33%" = a third wide, may shrink, never grows to fill. The
            shared Card already carries the dashboard's radius and shadow. */}
        {activeTab === "quickview" && <div style={{ display:"flex", gap:16, flexWrap:"wrap", alignItems:"flex-start" }}>
          <Card style={{ flex:"0 1 33%", minWidth:300 }}>
            <Panel title="Details" />
            <Row label="Rating" value={rs.rating || "—"} />
            <Row label="Company Name" value={rs.company_name || "—"} />
            <Row label="Title" value={rs.title || "—"} />
            <Row label="Full Name" value={rs.full_name || "—"} />
            <Row label="Contact Type" value={rs.contact_type || "—"} />
            <Row label="Contact Sub-Type" value={rs.contact_subtype || "—"} />
            <Row label="Region" value={rs.region || "—"} />
            <Row label="Address" value={[rs.street, rs.city, rs.state, rs.zipcode, rs.country].filter(Boolean).join(", ") || "—"} />
            <Row label="Referral Source Status" value={<Badge s={rs.status} />} />
            <Row label="Owner" value={rs.ownerName || "—"} />
          </Card>
          <Card style={{ flex:"0 1 33%", minWidth:300 }}>
            <Panel title="Contact" />
            <Row label="E-Mail" value={rs.email || "—"} />
            <Row label={(rs.phone1_type || "Phone 1")} value={rs.phone1 || "—"} />
            <Row label={(rs.phone2_type || "Phone 2")} value={rs.phone2 || "—"} />
            <Row label="Website" value={rs.website || "—"} />
            <Row label="Created By" value={rs.createdby || "—"} />
            <Row label="Last Modified" value={rs.modifiedat ? fmtDate(rs.modifiedat) : "—"} />
            {/* The date the follow-up cadence runs on, and WHY it is that date.
                Shown here rather than only on the Activity tab because this is
                the card a rep looks at before picking up the phone. */}
            <Row label="Last Touch" value={<LastTouchLine lasttouch={lastTouch} />} />
          </Card>
        </div>}

        {activeTab === "leads" && <>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
            {role.canEdit && <Btn variant="primary" onClick={() => setModal({ type:"lead-add", referralsourceid:rs.id, onDone:load })}>+ Add Lead</Btn>}
          </div>
          {leads.length === 0
            ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"8px 0" }}>No leads from this source yet.</div>
            : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                <thead><tr>
                  <STh label="Company" sortKey="companyname" currentKey={lKey} currentDir={lDir} onSort={lToggle} />
                  <STh label="Contact" sortKey="lastname"    currentKey={lKey} currentDir={lDir} onSort={lToggle} />
                  <STh label="Status"  sortKey="status"      currentKey={lKey} currentDir={lDir} onSort={lToggle} />
                  <STh label="Owner"   sortKey="ownerName"   currentKey={lKey} currentDir={lDir} onSort={lToggle} />
                  <STh label="Created" sortKey="createdat"   currentKey={lKey} currentDir={lDir} onSort={lToggle} />
                  <Th></Th>
                </tr></thead>
                <tbody>{sortedLeads.map(l => {
                  const converted = !!(l.converted_at || l.converted_accountid);
                  return (
                    <tr key={l.id} style={{ cursor:"pointer" }} onClick={() => setModal({ type: "lead-view", rec:l, onDone:load })}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <Td style={{ fontWeight:500 }}>{l.companyname || "—"}</Td>
                      <Td>{[l.firstname, l.lastname].filter(Boolean).join(" ") || "—"}</Td>
                      <Td>{converted ? <span style={{ fontSize:11, color:"#1a7f4b", fontWeight:600 }}>Converted</span> : (l.status || "—")}</Td>
                      <Td>{l.ownerName || "—"}</Td>
                      <Td>{fmtDate(l.createdat)}</Td>
                      <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                        <Btn onClick={() => setModal({ type: "lead-view", rec:l, onDone:load })}><Icon type="view" /></Btn>
                        {role.canEdit && !converted && <Btn onClick={() => setModal({ type:"lead-edit", rec:l, onDone:load })}><Icon type="edit" /></Btn>}
                        {role.canEdit && !converted && <Btn variant="primary" onClick={() => convert(l)}>Convert</Btn>}
                        {role.canDelete && <Btn onClick={() => setConfirm({ msg:"Delete this lead?", onConfirm:() => delRec("Leads", l.id, "Deleted lead", load) })} variant="danger"><Icon type="delete" /></Btn>}
                      </div></Td>
                    </tr>
                  );
                })}</tbody>
              </table>}
        </>}

        {activeTab === "opportunities" && (
          opps.length === 0
            ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"8px 0" }}>No opportunities from this source yet.</div>
            : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                <thead><tr>
                  <STh label="Opportunity" sortKey="name"        currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Account"     sortKey="companyname" currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Stage"       sortKey="stage_order" currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Owner"       sortKey="ownerName"   currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Commitment"  sortKey="commitment_amount" currentKey={oKey} currentDir={oDir} onSort={oToggle} numeric />
                </tr></thead>
                <tbody>{sortedOpps.map(o => (
                  <tr key={o.id} style={{ cursor:"pointer" }} onClick={() => setModal({ type: "opp-view", rec:o, accountid:o.accountid, onDone:load })}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <Td style={{ fontWeight:500 }}>{o.name || "—"}</Td>
                    <Td><PageLink page="sales-account" params={{ accountid:o.accountid, companyname:o.companyname }}>{o.companyname || "—"}</PageLink></Td>
                    <Td>{o.stage || "—"}</Td>
                    <Td>{o.ownerName || "—"}</Td>
                    <TdNum>{o.commitment_amount != null ? fmt(o.commitment_amount) : "—"}</TdNum>
                  </tr>
                ))}</tbody>
              </table>
        )}

        {activeTab === "activity" && <>
          {/* The old "Notes" tab was a single free-text field on the record —
              one box everybody overwrote, with no dates and no author. It is
              kept, read-only and clearly labelled, because it holds years of
              typed history nobody should lose. New contact goes in the stream
              below, where it is dated, attributed, and counted as a touch. */}
          {rs.notes && (
            <Card style={{ marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:6 }}>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--color-text-secondary)" }}>
                  Background (from the record)
                </span>
                {role.canEdit && <Btn onClick={() => setModal({ type:"referralsource-edit", rec:rs, onDone:load })}>Edit</Btn>}
              </div>
              <div style={{ fontSize:13, whiteSpace:"pre-wrap" }}>{rs.notes}</div>
            </Card>
          )}
          <SalesActivityPanel
            entitytype="ReferralSource"
            entityid={String(rs.id)}
            onLoaded={d => setLastTouch((d && d.lasttouch) || null)} />
        </>}

        {/* ── Email ──────────────────────────────────────────────────────────
            The captured correspondence on its own, in the same panel the
            contact and lead records use. Activity above still carries these
            messages alongside notes and follow-ups — that is the whole story
            in one stream. This tab is the one thread, at length, when the
            question is "what have we actually said to them".

            Referral sources only became email link targets in
            2026-09-24_referral_source_email_links.sql. Until that has run this
            reads "No captured email yet" whatever is in the mailbox, because
            there are no link rows to read. */}
        {activeTab === "email" && (
          <>
            {!rs.email && (
              <Card style={{ marginBottom:12 }}>
                <div style={{ fontSize:13 }}>
                  No email address on this referral source, so nothing can be matched to them.
                  {role.canEdit && <> Add one on the{" "}
                    <span onClick={() => setModal({ type:"referralsource-edit", rec:rs, onDone:load })}
                          style={{ color:B.cyanDark, cursor:"pointer", textDecoration:"underline" }}>
                      record</span>, then re-run the email match.</>}
                </div>
              </Card>
            )}
            <EmailActivityPanel entitytype="ReferralSource" entityid={String(rs.id)} />
          </>
        )}

      </div>
    </div>
  </>;
};
